/**
 * Weekly opening-hours bitmap: 7 days x 48 half-hour slots = 336 bits = 42 bytes,
 * base64-encoded to ~56 characters per venue.
 *
 * Why pre-bake instead of shipping the raw OSM `opening_hours` string to the phone:
 * that syntax is a small language ("Mo-Th 11:00-23:00; Fr 11:00-15:00; Sa sunset-01:00;
 * PH off") and re-parsing ~3000 of them on every time-slider tick would be both slow
 * and pointless — the answer never changes between pipeline runs.
 *
 * Day 0 is Sunday, matching both JS `Date.getDay()` and the Israeli week.
 */

export const SLOTS_PER_DAY = 48;
export const DAYS_PER_WEEK = 7;
export const TOTAL_SLOTS = SLOTS_PER_DAY * DAYS_PER_WEEK; // 336
export const MASK_BYTES = TOTAL_SLOTS / 8; // 42
export const MINUTES_PER_SLOT = 24 * 60 / SLOTS_PER_DAY; // 30

const B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function createEmptyMask(): Uint8Array {
  return new Uint8Array(MASK_BYTES);
}

export function setSlot(mask: Uint8Array, day: number, slot: number, value = true): void {
  const idx = day * SLOTS_PER_DAY + slot;
  if (idx < 0 || idx >= TOTAL_SLOTS) return;
  const byte = idx >> 3;
  const bit = 1 << (idx & 7);
  if (value) mask[byte]! |= bit;
  else mask[byte]! &= ~bit;
}

export function getSlot(mask: Uint8Array, day: number, slot: number): boolean {
  const idx = day * SLOTS_PER_DAY + slot;
  if (idx < 0 || idx >= TOTAL_SLOTS) return false;
  return (mask[idx >> 3]! & (1 << (idx & 7))) !== 0;
}

/**
 * Mark a venue open from `startMin` to `endMin` (minutes past midnight) on `day`.
 *
 * When `endMin <= startMin` the range is treated as crossing midnight and spills into
 * the following day — this is the common case in Tel Aviv, where "Fr 20:00-03:00" is
 * normal and where getting it wrong would silently blank out the most valuable hours
 * of the week.
 */
export function setRange(
  mask: Uint8Array,
  day: number,
  startMin: number,
  endMin: number,
): void {
  const startSlot = Math.floor(startMin / MINUTES_PER_SLOT);
  const endSlot = Math.ceil(endMin / MINUTES_PER_SLOT);

  if (endMin > startMin) {
    for (let s = startSlot; s < endSlot; s++) setSlot(mask, day, s, true);
    return;
  }

  // Crosses midnight: fill to end of this day, then continue into the next.
  for (let s = startSlot; s < SLOTS_PER_DAY; s++) setSlot(mask, day, s, true);
  const nextDay = (day + 1) % DAYS_PER_WEEK;
  for (let s = 0; s < endSlot; s++) setSlot(mask, nextDay, s, true);
}

/** Number of open half-hour slots in the week. Zero means the parse produced nothing. */
export function openSlotCount(mask: Uint8Array): number {
  let count = 0;
  for (let i = 0; i < TOTAL_SLOTS; i++) {
    if ((mask[i >> 3]! & (1 << (i & 7))) !== 0) count++;
  }
  return count;
}

export function encodeMask(mask: Uint8Array): string {
  let out = '';
  for (let i = 0; i < mask.length; i += 3) {
    const b0 = mask[i]!;
    const b1 = i + 1 < mask.length ? mask[i + 1]! : 0;
    const b2 = i + 2 < mask.length ? mask[i + 2]! : 0;
    const triple = (b0 << 16) | (b1 << 8) | b2;
    out += B64_CHARS[(triple >> 18) & 63];
    out += B64_CHARS[(triple >> 12) & 63];
    out += i + 1 < mask.length ? B64_CHARS[(triple >> 6) & 63] : '=';
    out += i + 2 < mask.length ? B64_CHARS[triple & 63] : '=';
  }
  return out;
}

export function decodeMask(encoded: string): Uint8Array {
  const mask = createEmptyMask();
  let byteIdx = 0;
  let acc = 0;
  let bits = 0;
  for (const ch of encoded) {
    if (ch === '=') break;
    const v = B64_CHARS.indexOf(ch);
    if (v < 0) continue;
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      if (byteIdx < MASK_BYTES) mask[byteIdx++] = (acc >> bits) & 0xff;
    }
  }
  return mask;
}

/**
 * Is this venue open at the given local day/time?
 *
 * `hours` is null when opening hours are unknown. The caller must supply
 * `unknownDefault` explicitly rather than getting a silent guess — roughly half of
 * Tel Aviv's OSM venues lack the tag, so this branch drives a large share of the map
 * and the choice deserves to be visible at every call site.
 */
export function isOpenAt(
  hours: string | null,
  day: number,
  minutesOfDay: number,
  unknownDefault: boolean,
): boolean {
  if (hours === null) return unknownDefault;
  const slot = Math.floor(minutesOfDay / MINUTES_PER_SLOT);
  return getSlot(decodeMask(hours), day, slot);
}
